import type { components } from "@brandex/api-client";
import { useBrandex, useMe } from "@brandex/dashboard";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Spinner, Textarea, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Lock, Pencil, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import { GROUPS, PERMISSIONS, can, useStaffAccess, type StaffPermission } from "../access";
import { QueryError, errorText, fieldError } from "../views";

type Roles = components["schemas"]["RolesDto"];
type CustomRole = components["schemas"]["CustomRoleDto"];
type BuiltinKey = components["schemas"]["BuiltinRoleKeyDto"];
type Member = components["schemas"]["StaffMemberDto"];
type Scope = components["schemas"]["ClientScopeDto"];

export const staffKeys = { roles: ["admin", "roles"] as const, staff: ["admin", "staff"] as const };

export type StaffTab = "staff" | "roles";
export function isStaffTab(v: unknown): v is StaffTab {
  return v === "staff" || v === "roles";
}

function useRoles(enabled: boolean) {
  const { api } = useBrandex();
  return useQuery<Roles>({
    queryKey: staffKeys.roles,
    enabled,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/roles");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

function useStaff(enabled: boolean) {
  const { api } = useBrandex();
  return useQuery<Member[]>({
    queryKey: staffKeys.staff,
    enabled,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/staff");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

/** The roles a member holds, by name. */
export function roleNames(m: Pick<Member, "builtin_roles" | "custom_roles">, roles: Roles | undefined): string[] {
  const builtin = m.builtin_roles.map((k) => roles?.builtin.find((b) => b.key === k)?.name ?? k);
  const custom = m.custom_roles.map((id) => roles?.custom.find((c) => c.id === id)?.name ?? "Removed role");
  return [...builtin, ...custom];
}

/**
 * Staff and roles (ADR 0022): who is on the team, what each role allows,
 * and custom roles. The server enforces every rule; this page shows them.
 */
export function StaffPage({ tab, go }: { tab: StaffTab; go: (tab: StaffTab) => void }) {
  const access = useStaffAccess();
  const manageRoles = can(access.data, "platform.roles.manage");
  const managePeople = can(access.data, "platform.users.manage");
  const roles = useRoles(manageRoles);
  if (access.isPending) return <Spinner label="Loading" />;
  if (!manageRoles && !managePeople) {
    return <Alert tone="error">Staff and roles need the Manage people or Manage roles permission.</Alert>;
  }
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Staff and roles</h1>
        <p className="text-sm text-muted-foreground">
          Who works on BrandEx, what each role allows and which clients they look after. Every change is in the audit log.
        </p>
      </header>
      <nav aria-label="Staff sections" className="flex gap-1 border-b border-border">
        {(
          [
            ["staff", "Staff"],
            ["roles", "Roles"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? "page" : undefined}
            onClick={() => go(key)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2 text-sm font-medium",
              tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "staff" ? (
        <StaffTabView roles={roles.data} manageRoles={manageRoles} managePeople={managePeople} />
      ) : manageRoles ? (
        <RolesTabView />
      ) : (
        <Alert tone="info">Roles need the Manage roles permission.</Alert>
      )}
    </div>
  );
}

// --------------------------------------------------------------- staff tab

function StaffTabView({ roles, manageRoles, managePeople }: { roles: Roles | undefined; manageRoles: boolean; managePeople: boolean }) {
  const staff = useStaff(managePeople);
  const me = useMe();
  const [inviting, setInviting] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Invited staff choose a password from their email, then set up an authenticator app at their first sign-in.</p>
        {manageRoles && roles ? (
          <Button onClick={() => { setInviting((v) => !v); setSent(null); }}>
            <UserPlus className="h-4 w-4" aria-hidden />
            Invite staff
          </Button>
        ) : null}
      </div>
      {sent ? <Alert tone="success">Invitation sent to {sent}.</Alert> : null}
      {inviting && roles ? <InviteForm roles={roles} onDone={(email) => { setInviting(false); setSent(email); }} /> : null}
      {!managePeople ? <Alert tone="info">The staff list needs the Manage people permission.</Alert> : null}
      {staff.isPending && managePeople ? <Spinner label="Loading staff" /> : null}
      {staff.isError ? <QueryError error={staff.error} /> : null}
      {staff.data ? (
        <ul className="grid gap-3">
          {staff.data.map((m) => (
            <StaffRow key={m.id} member={m} roles={roles} self={m.id === me.data?.id} manageRoles={manageRoles} managePeople={managePeople} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function statusBadge(m: Member) {
  if (m.status === "disabled") return <Badge tone="neutral">Disabled</Badge>;
  if (m.invited) return <Badge tone="warning">Invited</Badge>;
  return <Badge tone="success">Active</Badge>;
}

function StaffRow({ member: m, roles, self, manageRoles, managePeople }: { member: Member; roles: Roles | undefined; self: boolean; manageRoles: boolean; managePeople: boolean }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const status = useMutation({
    mutationFn: async (active: boolean) => {
      const { data } = await api.http.POST("/v1/admin/staff/{user_id}/status", { params: { path: { user_id: m.id } }, body: { active } });
      return data;
    },
    onSuccess: () => {
      setConfirmOff(false);
      void qc.invalidateQueries({ queryKey: staffKeys.staff });
    },
  });
  const names = roleNames(m, roles);
  return (
    <li className="grid gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {m.display_name} {self ? <span className="text-xs text-muted-foreground">(you)</span> : null}
          </p>
          <p className="truncate text-sm text-muted-foreground">{m.email}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {statusBadge(m)}
          <Badge tone={m.mfa_enabled ? "success" : "neutral"}>{m.mfa_enabled ? "2FA on" : "2FA not set up"}</Badge>
          <Badge>{m.clients.length === 1 ? "1 assigned client" : `${String(m.clients.length)} assigned clients`}</Badge>
        </div>
      </div>
      <ul aria-label={`Roles of ${m.display_name}`} className="flex flex-wrap gap-1.5">
        {names.map((n) => (
          <li key={n} className="rounded-[var(--radius-pill)] bg-muted px-2.5 py-0.5 text-xs font-medium">{n}</li>
        ))}
      </ul>
      {!self && (manageRoles || managePeople) ? (
        <div className="flex flex-wrap gap-2">
          {manageRoles && roles ? (
            <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit roles
            </Button>
          ) : null}
          {managePeople && m.status === "disabled" ? (
            <Button size="sm" variant="outline" loading={status.isPending} onClick={() => status.mutate(true)}>Enable</Button>
          ) : null}
          {managePeople && m.status !== "disabled" && !confirmOff ? (
            <Button size="sm" variant="ghost" onClick={() => setConfirmOff(true)}>Disable</Button>
          ) : null}
          {confirmOff ? (
            <span className="flex flex-wrap items-center gap-2 text-sm">
              Signs {m.display_name} out everywhere.
              <Button size="sm" variant="destructive" loading={status.isPending} onClick={() => status.mutate(false)}>Disable {m.display_name}</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmOff(false)}>Cancel</Button>
            </span>
          ) : null}
        </div>
      ) : null}
      {status.isError ? <Alert tone="error">{errorText(status.error)}</Alert> : null}
      {editing && roles ? <EditRoles member={m} roles={roles} onDone={() => setEditing(false)} /> : null}
    </li>
  );
}

interface Picked {
  builtin: BuiltinKey[];
  custom: string[];
}

function RolePicker({ roles, value, onChange, legend }: { roles: Roles; value: Picked; onChange: (v: Picked) => void; legend: string }) {
  const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-sm font-medium">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {roles.builtin.map((b) => (
          <label key={b.key} className="flex items-start gap-2 rounded-[var(--radius-md)] border border-border p-2.5 text-sm">
            <input type="checkbox" className="mt-0.5" checked={value.builtin.includes(b.key)} onChange={() => onChange({ ...value, builtin: toggle(value.builtin, b.key) })} />
            <span>
              <span className="font-medium">{b.name}</span> <span className="text-xs text-muted-foreground">built-in</span>
              <span className="block text-xs text-muted-foreground">{b.permissions.map((p) => PERMISSIONS[p].label).join(", ")}</span>
            </span>
          </label>
        ))}
        {roles.custom.map((c) => (
          <label key={c.id} className="flex items-start gap-2 rounded-[var(--radius-md)] border border-border p-2.5 text-sm">
            <input type="checkbox" className="mt-0.5" checked={value.custom.includes(c.id)} onChange={() => onChange({ ...value, custom: toggle(value.custom, c.id) })} />
            <span>
              <span className="font-medium">{c.name}</span> <span className="text-xs text-muted-foreground">{c.scope === "assigned" ? "assigned clients" : "all clients"}</span>
              <span className="block text-xs text-muted-foreground">{c.permissions.map((p) => PERMISSIONS[p].label).join(", ")}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function InviteForm({ roles, onDone }: { roles: Roles; onDone: (email: string) => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [locale, setLocale] = useState("en");
  const [picked, setPicked] = useState<Picked>({ builtin: [], custom: [] });
  const invite = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.POST("/v1/admin/staff", {
        body: { display_name: name.trim(), email: email.trim(), locale, builtin_roles: picked.builtin, custom_roles: picked.custom },
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.staff });
      void qc.invalidateQueries({ queryKey: staffKeys.roles });
      onDone(email.trim());
    },
  });
  const none = picked.builtin.length === 0 && picked.custom.length === 0;
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!none) invite.mutate();
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite staff</CardTitle>
        <CardDescription>They get an email to choose a password. Pick at least one role; you can only give roles within your own access.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name" error={fieldError(invite.error, "display_name")}>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label="Email" error={fieldError(invite.error, "email")}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Email language">
              <Select value={locale} onChange={(e) => setLocale(e.target.value)}>
                <option value="en">English</option>
                <option value="bn">বাংলা</option>
              </Select>
            </Field>
          </div>
          <RolePicker roles={roles} value={picked} onChange={setPicked} legend="Roles" />
          {invite.isError ? <Alert tone="error">{errorText(invite.error)}</Alert> : null}
          <div>
            <Button type="submit" disabled={none} loading={invite.isPending}>Send invitation</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EditRoles({ member, roles, onDone }: { member: Member; roles: Roles; onDone: () => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Picked>({ builtin: member.builtin_roles, custom: member.custom_roles.filter((id) => roles.custom.some((c) => c.id === id)) });
  const save = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.PUT("/v1/admin/staff/{user_id}/roles", {
        params: { path: { user_id: member.id } },
        body: { builtin_roles: picked.builtin, custom_roles: picked.custom },
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.staff });
      void qc.invalidateQueries({ queryKey: staffKeys.roles });
      onDone();
    },
  });
  return (
    <div className="grid gap-3 rounded-[var(--radius-md)] bg-muted/50 p-3">
      <RolePicker roles={roles} value={picked} onChange={setPicked} legend={`Roles of ${member.display_name}`} />
      <p className="text-xs text-muted-foreground">Saving signs them out so their new roles apply at their next sign-in.</p>
      {save.isError ? <Alert tone="error">{errorText(save.error)}</Alert> : null}
      <div className="flex gap-2">
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save roles</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- roles tab

interface Draft {
  id: string | null;
  name: string;
  description: string;
  scope: Scope;
  permissions: StaffPermission[];
}

const EMPTY: Draft = { id: null, name: "", description: "", scope: "all", permissions: [] };

function RolesTabView() {
  const roles = useRoles(true);
  const access = useStaffAccess();
  const [draft, setDraft] = useState<Draft | null>(null);
  if (roles.isPending) return <Spinner label="Loading roles" />;
  if (roles.isError) return <QueryError error={roles.error} />;
  const r = roles.data;
  return (
    <div className="grid gap-6">
      <section aria-labelledby="custom-roles" className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="custom-roles" className="font-display text-lg font-semibold">Custom roles</h2>
          <Button onClick={() => setDraft({ ...EMPTY })}>
            <Plus className="h-4 w-4" aria-hidden />
            New role
          </Button>
        </div>
        {draft ? <RoleEditor key={draft.id ?? "new"} initial={draft} held={access.data?.grants.map((g) => g.permission) ?? []} onDone={() => setDraft(null)} /> : null}
        {r.custom.length === 0 ? (
          <p className="text-sm text-muted-foreground">No custom roles yet. Start from scratch, or copy a built-in role below.</p>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {r.custom.map((c) => (
              <CustomRoleCard key={c.id} role={c} onEdit={() => setDraft({ id: c.id, name: c.name, description: c.description, scope: c.scope, permissions: c.permissions })} />
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="builtin-roles" className="grid gap-3">
        <h2 id="builtin-roles" className="font-display text-lg font-semibold">Built-in roles</h2>
        <p className="text-sm text-muted-foreground">Fixed and tested; they reach every client. Copy one to make a variation.</p>
        <ul className="grid gap-3 md:grid-cols-2">
          {r.builtin.map((b) => (
            <li key={b.key} className="grid gap-2 rounded-[var(--radius-lg)] border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-medium">
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {b.name}
                </p>
                <span className="text-xs text-muted-foreground">{b.holders === 1 ? "1 person" : `${String(b.holders)} people`}</span>
              </div>
              <PermissionChips permissions={b.permissions} />
              {b.key !== "super_admin" ? (
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDraft({ id: null, name: `${b.name} (copy)`, description: "", scope: "all", permissions: b.permissions })}
                  >
                    <Copy className="h-4 w-4" aria-hidden />
                    Copy as new role
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function PermissionChips({ permissions }: { permissions: StaffPermission[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {permissions.map((p) => (
        <li key={p} title={PERMISSIONS[p].description} className="rounded-[var(--radius-pill)] bg-muted px-2 py-0.5 text-xs">{PERMISSIONS[p].label}</li>
      ))}
    </ul>
  );
}

function CustomRoleCard({ role: c, onEdit }: { role: CustomRole; onEdit: () => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: async () => {
      await api.http.DELETE("/v1/admin/roles/{role_id}", { params: { path: { role_id: c.id } } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: staffKeys.roles }),
  });
  return (
    <li className="grid gap-2 rounded-[var(--radius-lg)] border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{c.name}</p>
        <div className="flex items-center gap-2 text-xs">
          <Badge tone={c.scope === "assigned" ? "warning" : "neutral"}>{c.scope === "assigned" ? "Assigned clients only" : "All clients"}</Badge>
          <span className="text-muted-foreground">{c.holders === 1 ? "1 person" : `${String(c.holders)} people`}</span>
        </div>
      </div>
      {c.description ? <p className="text-sm text-muted-foreground">{c.description}</p> : null}
      <PermissionChips permissions={c.permissions} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil className="h-4 w-4" aria-hidden />
          Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={c.holders > 0}
          title={c.holders > 0 ? "Take the role away from everyone first" : undefined}
          loading={remove.isPending}
          onClick={() => remove.mutate()}
          aria-label={`Delete ${c.name}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
          Delete
        </Button>
      </div>
      {remove.isError ? <Alert tone="error">{errorText(remove.error)}</Alert> : null}
    </li>
  );
}

function RoleEditor({ initial, held, onDone }: { initial: Draft; held: StaffPermission[]; onDone: () => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(initial);
  const save = useMutation({
    mutationFn: async () => {
      const body = { name: d.name.trim(), description: d.description.trim(), scope: d.scope, permissions: d.permissions };
      if (d.id === null) {
        const { data } = await api.http.POST("/v1/admin/roles", { body });
        return data;
      }
      const { data } = await api.http.PUT("/v1/admin/roles/{role_id}", { params: { path: { role_id: d.id } }, body });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.roles });
      onDone();
    },
  });
  const toggle = (p: StaffPermission) =>
    setD((v) => ({ ...v, permissions: v.permissions.includes(p) ? v.permissions.filter((x) => x !== p) : [...v.permissions, p] }));
  return (
    <Card>
      <CardHeader>
        <CardTitle>{d.id === null ? "New role" : `Edit ${initial.name}`}</CardTitle>
        <CardDescription>
          {d.id === null ? "Staff get these permissions on top of their other roles." : "Everyone holding this role gets the change on their next click, without signing in again."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" error={fieldError(save.error, "name")}>
              <Input value={d.name} maxLength={60} onChange={(e) => setD({ ...d, name: e.target.value })} required />
            </Field>
            <Field label="Description (optional)" error={fieldError(save.error, "description")}>
              <Textarea rows={2} maxLength={300} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
            </Field>
          </div>
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">Which clients</legend>
            {(
              [
                ["all", "All clients", "Reaches every client."],
                ["assigned", "Assigned clients only", "Account managers: reaches only the clients they are assigned to on each client's page. Works for client pages only, not platform-wide lists."],
              ] as const
            ).map(([value, label, hint]) => (
              <label key={value} className="flex items-start gap-2 text-sm">
                <input type="radio" name="scope" className="mt-1" checked={d.scope === value} onChange={() => setD({ ...d, scope: value })} />
                <span>
                  <span className="font-medium">{label}</span>
                  <span className="block text-xs text-muted-foreground">{hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <fieldset className="grid gap-4">
            <legend className="mb-1 text-sm font-medium">Permissions</legend>
            {GROUPS.map((g) => (
              <div key={g} className="grid gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(Object.keys(PERMISSIONS) as StaffPermission[])
                    .filter((p) => PERMISSIONS[p].group === g)
                    .map((p) => {
                      const mine = held.includes(p);
                      return (
                        <label key={p} className={cn("flex items-start gap-2 rounded-[var(--radius-md)] border border-border p-2.5 text-sm", !mine && "opacity-60")}>
                          <input type="checkbox" className="mt-0.5" disabled={!mine} checked={d.permissions.includes(p)} onChange={() => toggle(p)} />
                          <span>
                            <span className="font-medium">{PERMISSIONS[p].label}</span>
                            <span className="block text-xs text-muted-foreground">{mine ? PERMISSIONS[p].description : "You don't have this permission, so you can't give it."}</span>
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            ))}
          </fieldset>
          <p className="flex gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
            Approving content, billing and a practice&apos;s team always stay with the doctor and can&apos;t be put in a staff role.
          </p>
          {save.isError ? <Alert tone="error">{errorText(save.error)}</Alert> : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={d.permissions.length === 0} loading={save.isPending}>{d.id === null ? "Create role" : "Save role"}</Button>
            <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
